import { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import PublicNav from '../../components/PublicNav';
import { BlurFade } from '../../components/ui/blur-fade';
import { TextReveal } from '../../components/ui/text-reveal';
import { NumberTicker } from '../../components/ui/number-ticker';
import { Magnetic } from '../../components/ui/magnetic';
import { VARIANTS, VARIANT_LABELS } from './hero-variants';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Award,
  BarChart3,
  Bell,
  BookOpen,
  Briefcase,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  FileHeart,
  FlaskConical,
  GraduationCap,
  Layers,
  MessageSquare,
  Microscope,
  PenLine,
  RotateCcw,
  Shield,
  Sparkles,
  Stethoscope,
  TrendingUp,
  Users,
} from 'lucide-react';

const CAMPUS_IMG = 'https://images.pexels.com/photos/3985153/pexels-photo-3985153.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

/* ─── Data ──────────────────────────────────────────────────────────── */

const moduleCards = [
  { icon: CalendarDays, title: 'Attendance & Sessions', desc: 'Live session management, attendance marking, summary analytics, low-attendance monitoring, and remedial follow-up.', tone: 'from-[#214536] to-[#35614D]', slug: 'attendance' },
  { icon: Stethoscope, title: 'Clinical Postings', desc: 'Auto-scheduled practical rotation postings with session plans, curriculum mapping, and posting-level attendance.', tone: 'from-[#0F4C5C] to-[#1B7A8A]', slug: 'clinical-postings' },
  { icon: GraduationCap, title: 'Internal Assessments', desc: 'Category-aware IA — Theory, Clinical, Practical — with per-category pass rules, bulk entry, and eligibility computation.', tone: 'from-[#8A5528] to-[#C27A34]', slug: 'ia-tracker' },
  { icon: RotateCcw, title: 'CRMI Rotations', desc: 'Schedule internship rotations across departments, assign batches, track daily attendance per posting.', tone: 'from-[#2D3E63] to-[#446090]', slug: 'crmi' },
  { icon: Layers, title: 'Elective Management', desc: 'Events, subject catalogues, student self-registration, session scheduling, marks, and attendance tracking.', tone: 'from-[#5A2D8C] to-[#7B52C5]', slug: 'electives' },
  { icon: Briefcase, title: 'Faculty Management', desc: 'Faculty sessions, leave requests, conference tracking, personal inbox, and effectiveness dashboards.', tone: 'from-[#8A5528] to-[#C27A34]', slug: 'faculty' },
  { icon: MessageSquare, title: 'Feedback & Surveys', desc: 'Anonymous course and faculty feedback with threshold-protected results, release controls, and aggregated analytics.', tone: 'from-[#4A2D82] to-[#7B52C5]', slug: 'feedback' },
  { icon: FileHeart, title: 'Wellness & Discipline', desc: 'Mentorship, counseling, conduct records, anti-ragging compliance, and protected support workflows.', tone: 'from-[#7C3041] to-[#B14961]', slug: 'wellness' },
  { icon: AlertTriangle, title: 'Early Warning', desc: 'Risk scoring across attendance, IA, and wellness data — flag at-risk students before it becomes a crisis.', tone: 'from-[#7A2D20] to-[#C04B38]', slug: 'early-warning' },
  { icon: Award, title: 'Outreach & Activities', desc: 'Log extracurricular, community outreach, and co-curricular activities per student — visible on their full profile.', tone: 'from-[#5C4A10] to-[#9A7A22]', slug: 'outreach' },
  { icon: FlaskConical, title: 'Research Activities', desc: 'Department research register — funded projects, IEC approval tracking, monthly reporting, and secure proof documents.', tone: 'from-[#1F3C2F] to-[#3E6B54]', slug: 'research' },
  { icon: Microscope, title: 'PG Resident Tracking', desc: 'Postgraduate activity logbook with guide verification, thesis milestones, and dual-role faculty resident support.', tone: 'from-[#2C4C3B] to-[#4A7A5E]', slug: 'pg-residents' },
];

const valuePillars = [
  { icon: Users, title: 'One Student Record', desc: 'Track the student journey across attendance, academics, wellbeing, and compliance without splitting context across separate tools.' },
  { icon: BookOpen, title: 'Medical College Ready', desc: 'Built around departments, batches, postings, internal assessments, practicals, and clinical workflow patterns.' },
  { icon: BarChart3, title: 'Decision-Ready Visibility', desc: 'Give clerks, faculty, and college admins a role-specific view into the same institution data with the right scope.' },
];

const workflow = [
  { step: '01', title: 'Set up the academic structure', desc: 'Configure academic years, levels, departments, categories, time slots, batches, leave types, and user access scopes.' },
  { step: '02', title: 'Run daily academic operations', desc: 'Create sessions, mark attendance, track low-attendance risk, manage faculty sessions and leaves, and act on early warnings.' },
  { step: '03', title: 'Track assessments and feedback', desc: 'Enter IA marks by category, run anonymous course and faculty surveys, and see per-category pass/fail instantly.' },
  { step: '04', title: 'Review eligibility and pulse', desc: 'Cross IA performance with attendance to compute exam eligibility, view the institution pulse, and act early rather than react late.' },
];

const MOCK_IA_CATS = [
  { id: 'theory', label: 'Theory', passThreshold: 15, ia1Max: 20, ia2Max: 20, bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-400' },
  { id: 'clinical', label: 'Clinical', passThreshold: 10, ia1Max: 15, ia2Max: 15, bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', dot: 'bg-violet-400' },
];

const MOCK_STUDENTS = [
  { name: 'Arun Kumar', reg: 'MC2401', theory: { ia1: 17, ia2: 18, pass: true }, clinical: { ia1: 12, ia2: 13, pass: true }, overall: true },
  { name: 'Priya Sharma', reg: 'MC2402', theory: { ia1: 11, ia2: 12, pass: false }, clinical: { ia1: 12, ia2: 13, pass: true }, overall: false },
  { name: 'Sanjay Mehta', reg: 'MC2403', theory: { ia1: 19, ia2: 17, pass: true }, clinical: { ia1: 13, ia2: 14, pass: true }, overall: true },
  { name: 'Divya Nair', reg: 'MC2404', theory: { ia1: 16, ia2: 15, pass: true }, clinical: { ia1: 9, ia2: 10, pass: false }, overall: false },
];

const IA_CAPABILITIES = [
  { icon: ClipboardList, title: 'Category-Aware Exam Setup', desc: 'Define exams per category — Theory, Clinical, Practical — each with its own max marks and pass threshold.' },
  { icon: PenLine, title: 'Bulk Markbook Entry', desc: 'Enter all student marks across every category in a single grid. Save the entire batch in one click.' },
  { icon: ClipboardCheck, title: 'Per-Category Pass Requirement', desc: 'Students must pass every category independently. Failing Theory alone means overall fail — total marks don\'t compensate.' },
  { icon: TrendingUp, title: 'Eligibility Crossover', desc: 'IA results feed directly into eligibility computation alongside attendance — one platform, one decision view.' },
];

const MOCK_FEEDBACK_RESULTS = [
  { label: 'Course content clarity', mean: 4.2, dist: [2, 3, 8, 22, 15], total: 50 },
  { label: 'Pace and delivery', mean: 3.8, dist: [1, 6, 12, 20, 11], total: 50 },
  { label: 'Materials provided', mean: 4.5, dist: [0, 2, 5, 18, 25], total: 50 },
];

/* ─── Animated Card Wrapper ────────────────────────────────────────── */

function AnimatedCard({ children, className = '', delay = 0, whileHover = true }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20, filter: 'blur(4px)' }}
      animate={isInView ? { opacity: 1, y: 0, filter: 'blur(0px)' } : undefined}
      transition={{ duration: 0.5, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
      whileHover={whileHover ? { y: -4, transition: { duration: 0.25, ease: 'easeOut' } } : undefined}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Section Header Component ─────────────────────────────────────── */

function SectionHeader({ badge, badgeColor, title, subtitle, className = '' }) {
  return (
    <div className={`max-w-3xl ${className}`}>
      <BlurFade delay={0}>
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${badgeColor} text-xs tracking-[0.15em] uppercase font-bold mb-4`}>
          {badge}
        </div>
      </BlurFade>
      <BlurFade delay={0.1}>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1C1A]" style={{ fontFamily: 'Outfit' }}>
          {title}
        </h2>
      </BlurFade>
      {subtitle && (
        <BlurFade delay={0.2}>
          <p className="mt-4 text-base text-[#5C605C] max-w-2xl leading-relaxed">{subtitle}</p>
        </BlurFade>
      )}
    </div>
  );
}

/* ─── Page ─────────────────────────────────────────────────────────── */

export default function LandingPage() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  /* ── Hero variant cycling ── */
  const CYCLE_MS = 10000;
  const [activeIdx, setActiveIdx] = useState(0);
  const timerRef = useRef(null);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % VARIANTS.length);
    }, CYCLE_MS);
  }, []);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  const goToVariant = (idx) => {
    setActiveIdx(idx);
    startTimer(); // reset timer on manual click
  };

  const ActiveHero = VARIANTS[activeIdx];

  return (
    <div className="min-h-screen bg-[#F4F5F0]" data-testid="landing-page">
      <PublicNav />

      {/* ── Hero — left stays, right cycles through 5 visuals ──────── */}
      <section ref={heroRef} className="relative overflow-hidden pt-28 pb-24 px-6" data-testid="hero-section">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,_rgba(95,138,116,0.12),_transparent_50%),radial-gradient(circle_at_80%_80%,_rgba(194,122,52,0.1),_transparent_40%)]" />
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
            backgroundRepeat: 'repeat',
            backgroundSize: '128px 128px',
          }}
        />

        <motion.div style={{ opacity: heroOpacity }} className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* ── Left column — always visible ── */}
          <div className="lg:col-span-7 space-y-8">
            <BlurFade delay={0.1}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1F3C2F]/8 border border-[#1F3C2F]/10 text-[#1F3C2F] text-xs tracking-[0.15em] uppercase font-bold">
                <Sparkles className="w-3 h-3" />
                Operations Platform for Medical Colleges
              </div>
            </BlurFade>

            <div className="space-y-5">
              <TextReveal
                delay={0.2}
                as="h1"
                className="text-4xl sm:text-5xl lg:text-[3.5rem] xl:text-6xl tracking-tighter font-light text-[#1A1C1A] leading-[1.03]"
              >
                One platform for attendance, academics, faculty, feedback, and governance.
              </TextReveal>

              <BlurFade delay={0.6}>
                <p className="text-base lg:text-lg text-[#5C605C] leading-relaxed max-w-2xl">
                  CliniQ 360 is the operating layer for a medical college — connecting daily attendance work with internal assessments, faculty lifecycle, anonymous surveys, early warning, timetabling, and student support in one coherent platform.
                </p>
              </BlurFade>
            </div>

            <BlurFade delay={0.7}>
              <div className="flex flex-col sm:flex-row gap-4">
                <Magnetic strength={0.15}>
                  <Button
                    data-testid="hero-get-started-btn"
                    className="bg-[#1F3C2F] hover:bg-[#325744] text-white h-12 px-8 text-base shadow-[0_1px_3px_rgba(31,60,47,0.3),0_8px_24px_rgba(31,60,47,0.15),inset_0_1px_0_rgba(255,255,255,0.1)] hover:shadow-[0_1px_3px_rgba(31,60,47,0.3),0_12px_32px_rgba(31,60,47,0.2),inset_0_1px_0_rgba(255,255,255,0.1)] transition-shadow"
                    onClick={() => navigate('/login')}
                  >
                    Open Platform <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Magnetic>
                <Button
                  variant="outline"
                  className="border-stone-300 bg-white/70 text-[#1A1C1A] h-12 px-8 text-base hover:bg-white hover:border-stone-400 transition-all"
                  onClick={() => document.getElementById('modules')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Explore 10 Modules
                </Button>
              </div>
            </BlurFade>

            <div className="grid gap-3 sm:grid-cols-3">
              {[
                'Attendance, IA, and faculty operations in one workflow',
                'Anonymous feedback with threshold-protected results',
                'Early warning across attendance, marks, and wellness',
              ].map((item, i) => (
                <BlurFade key={item} delay={0.8 + i * 0.1}>
                  <div className="flex items-start gap-2 rounded-2xl border border-stone-200/80 bg-white/60 backdrop-blur-sm p-3 text-sm text-[#5C605C] hover:bg-white/90 hover:border-stone-300 transition-all duration-300">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#1F3C2F] shrink-0" />
                    <span>{item}</span>
                  </div>
                </BlurFade>
              ))}
            </div>
          </div>

          {/* ── Right column — cycles through 5 visuals ── */}
          <div className="lg:col-span-5">
            <BlurFade delay={0.4}>
              <div className="relative min-h-[430px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIdx}
                    initial={{ opacity: 0, scale: 0.97, filter: 'blur(8px)' }}
                    animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, scale: 0.97, filter: 'blur(8px)' }}
                    transition={{ duration: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
                  >
                    <ActiveHero />
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Progress indicators */}
              <div className="flex items-center justify-center gap-1.5 mt-5">
                {VARIANT_LABELS.map((label, i) => (
                  <button
                    key={label}
                    onClick={() => goToVariant(i)}
                    className={`relative h-1.5 rounded-full overflow-hidden transition-all duration-300 ${i === activeIdx ? 'w-16 bg-stone-200' : 'w-6 bg-stone-200 hover:bg-stone-300'
                      }`}
                    title={label}
                  >
                    {i === activeIdx && (
                      <motion.span
                        className="absolute inset-y-0 left-0 rounded-full bg-[#1F3C2F]"
                        initial={{ width: '0%' }}
                        animate={{ width: '100%' }}
                        transition={{ duration: CYCLE_MS / 1000, ease: 'linear' }}
                        key={`bar-${activeIdx}`}
                      />
                    )}
                    {i !== activeIdx && (
                      <span className={`absolute inset-0 rounded-full ${i < activeIdx ? 'bg-[#1F3C2F]/40' : ''}`} />
                    )}
                  </button>
                ))}
              </div>
            </BlurFade>
          </div>
        </motion.div>
      </section>

      {/* ── Platform Modules ─────────────────────────────────────────── */}
      <section id="modules" className="py-24 px-6 bg-white" data-testid="modules-section">
        <div className="max-w-7xl mx-auto space-y-14">
          <SectionHeader
            badge="10 Platform Modules"
            badgeColor="bg-stone-100 text-stone-500"
            title="Everything a medical college needs — in one coherent system"
            subtitle="From daily attendance to anonymous feedback, faculty leave to early risk detection — each module is purpose-built for the workflows a medical college actually runs."
          />

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {moduleCards.map((module, index) => (
              <AnimatedCard key={module.title} delay={index * 0.06} className="h-full">
                <Link
                  to={`/features/${module.slug}`}
                  className="group block rounded-[1.5rem] border border-stone-200/80 overflow-hidden bg-[#FCFCFA] h-full hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] hover:border-stone-300 transition-all duration-300"
                  data-testid={`module-card-${index}`}
                >
                  <div className={`h-1 bg-gradient-to-r ${module.tone} opacity-80 group-hover:opacity-100 group-hover:h-1.5 transition-all duration-300`} />
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${module.tone} text-white flex items-center justify-center group-hover:scale-110 group-hover:shadow-lg transition-all duration-300`}>
                        <module.icon className="w-4 h-4" />
                      </div>
                      <ArrowRight className="w-4 h-4 text-stone-300 translate-x-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 mt-1" />
                    </div>
                    <h3 className="text-sm font-semibold text-[#1A1C1A]" style={{ fontFamily: 'Outfit' }}>{module.title}</h3>
                    <p className="mt-2 text-xs leading-relaxed text-[#5C605C]">{module.desc}</p>
                  </div>
                </Link>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* ── IA Tracker Feature Section ───────────────────────────────── */}
      <section id="ia-tracker" className="py-24 px-6" data-testid="ia-section">
        <div className="max-w-7xl mx-auto space-y-14">
          <SectionHeader
            badge="Internal Assessment Tracker"
            badgeColor="bg-[#7A4A23]/10 text-[#7A4A23]"
            title="Category-aware IA marks — Theory, Clinical, Practical tracked independently"
            subtitle="Each subject's categories run their own exams with independent pass thresholds. Students must clear every category — and the markbook shows you exactly where they stand."
          />

          <div className="grid gap-10 lg:grid-cols-2 items-start">
            {/* Markbook preview */}
            <AnimatedCard whileHover={false} className="overflow-hidden">
              <div className="rounded-[1.75rem] border border-stone-200/80 bg-white overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
                <div className="bg-gradient-to-r from-[#8A5528] to-[#C27A34] px-6 py-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.14em] text-white/70">IA Markbook · Category View</p>
                    <p className="text-white font-medium mt-0.5" style={{ fontFamily: 'Outfit' }}>General Medicine — MBBS Year II</p>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-xs bg-blue-400/30 text-white px-3 py-1 rounded-full backdrop-blur-sm">Theory</span>
                    <span className="text-xs bg-violet-400/30 text-white px-3 py-1 rounded-full backdrop-blur-sm">Clinical</span>
                  </div>
                </div>

                <div className="flex gap-4 px-6 py-2.5 bg-[#FFF8EE] border-b border-stone-100">
                  {MOCK_IA_CATS.map((cat) => (
                    <div key={cat.id} className="flex items-center gap-2 text-xs">
                      <span className={`w-2 h-2 rounded-full ${cat.dot}`} />
                      <span className={cat.text + ' font-semibold'}>{cat.label}</span>
                      <span className="text-[#9CA3AF]">pass ≥ {cat.passThreshold} / {cat.ia1Max + cat.ia2Max}</span>
                    </div>
                  ))}
                  <div className="ml-auto flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                    <ClipboardList className="w-3 h-3" />
                    Must pass each category
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-100">
                        <th className="text-left px-4 py-2 text-xs font-semibold text-[#5C605C] bg-stone-50" rowSpan={2}>Student</th>
                        <th colSpan={3} className="text-center px-2 py-2 text-xs font-bold text-blue-700 bg-blue-50 border-l border-blue-100">Theory</th>
                        <th colSpan={3} className="text-center px-2 py-2 text-xs font-bold text-violet-700 bg-violet-50 border-l border-violet-100">Clinical</th>
                        <th className="text-center px-3 py-2 text-xs font-semibold text-[#5C605C] bg-stone-50 border-l border-stone-100" rowSpan={2}>Overall</th>
                      </tr>
                      <tr className="border-b border-stone-100">
                        <th className="text-center px-2 py-1.5 text-xs text-[#5C605C] bg-blue-50 border-l border-blue-100">IA 1</th>
                        <th className="text-center px-2 py-1.5 text-xs text-[#5C605C] bg-blue-50">IA 2</th>
                        <th className="text-center px-2 py-1.5 text-xs text-[#5C605C] bg-blue-50">Status</th>
                        <th className="text-center px-2 py-1.5 text-xs text-[#5C605C] bg-violet-50 border-l border-violet-100">IA 1</th>
                        <th className="text-center px-2 py-1.5 text-xs text-[#5C605C] bg-violet-50">IA 2</th>
                        <th className="text-center px-2 py-1.5 text-xs text-[#5C605C] bg-violet-50">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {MOCK_STUDENTS.map((s, i) => (
                        <tr key={s.reg} className={`border-b border-stone-50 ${i % 2 === 1 ? 'bg-stone-50/40' : ''}`}>
                          <td className="px-4 py-2.5">
                            <div className="font-medium text-[#1A1C1A] text-sm">{s.name}</div>
                            <div className="text-xs text-[#9CA3AF]">{s.reg}</div>
                          </td>
                          <td className="text-center px-2 py-2.5 border-l border-blue-50">
                            <span className="inline-block w-7 h-6 rounded bg-blue-50 text-blue-800 text-xs font-mono leading-6">{s.theory.ia1}</span>
                          </td>
                          <td className="text-center px-2 py-2.5">
                            <span className="inline-block w-7 h-6 rounded bg-blue-50 text-blue-800 text-xs font-mono leading-6">{s.theory.ia2}</span>
                          </td>
                          <td className="text-center px-2 py-2.5">
                            {s.theory.pass
                              ? <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-semibold"><CheckCircle2 className="w-3 h-3" />P</span>
                              : <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold">✕ F</span>}
                          </td>
                          <td className="text-center px-2 py-2.5 border-l border-violet-50">
                            <span className="inline-block w-7 h-6 rounded bg-violet-50 text-violet-800 text-xs font-mono leading-6">{s.clinical.ia1}</span>
                          </td>
                          <td className="text-center px-2 py-2.5">
                            <span className="inline-block w-7 h-6 rounded bg-violet-50 text-violet-800 text-xs font-mono leading-6">{s.clinical.ia2}</span>
                          </td>
                          <td className="text-center px-2 py-2.5">
                            {s.clinical.pass
                              ? <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-semibold"><CheckCircle2 className="w-3 h-3" />P</span>
                              : <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold">✕ F</span>}
                          </td>
                          <td className="text-center px-3 py-2.5 border-l border-stone-100">
                            {s.overall
                              ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-semibold"><CheckCircle2 className="w-3 h-3" /> Pass</span>
                              : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold">✕ Fail</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="px-6 py-3 bg-stone-50 border-t border-stone-100 flex items-center justify-between">
                  <span className="text-xs text-[#5C605C]">4 students · 2 fully passed · 2 failed a category</span>
                  <button className="text-xs bg-[#7A4A23] text-white px-4 py-1.5 rounded-lg font-medium hover:bg-[#8A5528] transition-colors">
                    Save All Marks
                  </button>
                </div>
              </div>
            </AnimatedCard>

            {/* Capabilities */}
            <div className="space-y-5">
              {IA_CAPABILITIES.map((cap, i) => (
                <AnimatedCard key={cap.title} delay={i * 0.08}>
                  <div className="rounded-[1.5rem] border border-stone-200/80 bg-white p-5 flex gap-4 hover:shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:border-stone-300 transition-all duration-300">
                    <div className="w-11 h-11 rounded-2xl bg-[#7A4A23]/10 text-[#7A4A23] flex items-center justify-center shrink-0">
                      <cap.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-medium text-[#1A1C1A]" style={{ fontFamily: 'Outfit' }}>{cap.title}</h3>
                      <p className="mt-1.5 text-sm text-[#5C605C] leading-relaxed">{cap.desc}</p>
                    </div>
                  </div>
                </AnimatedCard>
              ))}

              <div className="grid grid-cols-3 gap-3 pt-1">
                {[
                  { val: 'Category', label: 'Aware Exams' },
                  { val: 'Bulk', label: 'Mark Entry' },
                  { val: 'Live', label: 'Pass / Fail' },
                ].map((stat, i) => (
                  <AnimatedCard key={stat.label} delay={0.4 + i * 0.08}>
                    <div className="rounded-2xl border border-[#F1DFC0] bg-[#FFF8EE] p-4 text-center">
                      <div className="text-xl font-semibold text-[#7A4A23]" style={{ fontFamily: 'Outfit' }}>{stat.val}</div>
                      <div className="text-xs text-[#9A7A58] mt-1">{stat.label}</div>
                    </div>
                  </AnimatedCard>
                ))}
              </div>

              <BlurFade delay={0.5}>
                <div className="rounded-2xl border border-[#F1DFC0] bg-[#FFF8EE] p-5">
                  <div className="flex items-center gap-2 text-[#7A4A23] mb-2">
                    <GraduationCap className="w-4 h-4" />
                    <span className="text-sm font-semibold">Tied to Eligibility</span>
                  </div>
                  <p className="text-sm text-[#6A5A46] leading-relaxed">
                    IA marks feed directly into the eligibility computation alongside attendance. Admins can snapshot eligibility per level and download reports — all from the same platform.
                  </p>
                </div>
              </BlurFade>
            </div>
          </div>
        </div>
      </section>

      {/* ── CRMI Feature Section ─────────────────────────────────────── */}
      <section id="crmi" className="py-24 px-6 bg-white" data-testid="crmi-section">
        <div className="max-w-7xl mx-auto space-y-14">
          <SectionHeader
            badge="Clinical Rotations"
            badgeColor="bg-[#0F4C5C]/10 text-[#0F4C5C]"
            title="CRMI — Internship rotation scheduling and attendance, built in"
            subtitle="Create rotation schedules for multiple batches, assign students, define department posting windows, and mark daily attendance — all inside the same platform your college already operates in."
          />

          <div className="grid gap-8 lg:grid-cols-2 items-start">
            <AnimatedCard whileHover={false}>
              <div className="rounded-[1.75rem] border border-stone-200/80 bg-white overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
                <div className="bg-gradient-to-r from-[#0F4C5C] to-[#1B7A8A] px-6 py-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.14em] text-white/70">CRMI Schedule</p>
                    <p className="text-white font-medium mt-0.5" style={{ fontFamily: 'Outfit' }}>CRMI 2020 Batch — A1</p>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-xs bg-white/20 text-white px-3 py-1 rounded-full backdrop-blur-sm">16 students</span>
                    <span className="text-xs bg-white/20 text-white px-3 py-1 rounded-full backdrop-blur-sm">9 batches</span>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-stone-50 border-b border-stone-100">
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#5C605C]">Department</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-[#5C605C]">Period</th>
                        <th className="text-center px-3 py-2.5 text-xs font-semibold text-[#5C605C]">Weeks</th>
                        <th className="text-center px-3 py-2.5 text-xs font-semibold text-[#5C605C]">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { dept: 'Community Medicine', period: 'May–Jul 2025', wk: 6, status: 'completed' },
                        { dept: 'General Surgery', period: 'Jul–Sep 2025', wk: 6, status: 'completed' },
                        { dept: 'Paediatrics', period: 'Oct–Nov 2025', wk: 3, status: 'active' },
                        { dept: 'Anaesthesia', period: 'Nov–Dec 2025', wk: 2, status: 'upcoming' },
                      ].map((row, i) => (
                        <tr key={row.dept} className={`border-b border-stone-50 ${i % 2 === 1 ? 'bg-stone-50/50' : ''}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {row.status === 'active' && <span className="w-1.5 h-1.5 rounded-full bg-[#1B7A8A] animate-pulse" />}
                              <span className="font-medium text-[#1A1C1A] text-xs">{row.dept}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-xs text-[#5C605C]">{row.period}</td>
                          <td className="px-3 py-3 text-center text-xs text-[#5C605C]">{row.wk}w</td>
                          <td className="px-3 py-3 text-center">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${row.status === 'active' ? 'bg-[#E8F4F0] text-[#0F4C5C]' :
                              row.status === 'completed' ? 'bg-green-50 text-green-700' : 'bg-stone-100 text-stone-500'}`}>{row.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="grid grid-cols-3 gap-3 p-4 bg-stone-50 border-t border-stone-100">
                  {[
                    { val: 9, label: 'Rotation Batches' },
                    { val: 145, label: 'Students' },
                    { val: 18, label: 'Departments' },
                  ].map(stat => (
                    <div key={stat.label} className="rounded-xl border border-[#B2D8E0] bg-[#E8F4F0] p-3 text-center">
                      <div className="text-lg font-semibold text-[#0F4C5C]">
                        <NumberTicker value={stat.val} />
                      </div>
                      <div className="text-xs text-[#0F4C5C]/70 mt-0.5">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedCard>

            <div className="space-y-4">
              {[
                { icon: RotateCcw, title: 'Multi-Batch Rotation Scheduling', desc: 'Create staggered rotation schedules for A1–C3 batches. Each batch moves through the same departments at different times, keeping every ward occupied.' },
                { icon: CalendarDays, title: 'Daily Attendance Per Posting', desc: 'Mark each student present, absent, on leave, or on duty per day within their posting window. Date navigation stays locked to the posting period.' },
                { icon: BarChart3, title: 'Per-Student Rotation Dashboard', desc: 'Every student sees their full rotation schedule, current posting, and attendance percentage per department — all in one place.' },
                { icon: Stethoscope, title: 'Separate from Regular Attendance', desc: 'CRMI attendance is independent from classroom session attendance — different tracking, different reports, no cross-contamination.' },
              ].map((f, i) => (
                <AnimatedCard key={f.title} delay={i * 0.08}>
                  <div className="rounded-[1.5rem] border border-stone-200/80 bg-[#FCFCFA] p-5 flex gap-4 hover:shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:border-stone-300 transition-all duration-300">
                    <div className="w-10 h-10 rounded-xl bg-[#0F4C5C]/10 text-[#0F4C5C] flex items-center justify-center shrink-0">
                      <f.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-[#1A1C1A]">{f.title}</h3>
                      <p className="text-xs text-[#5C605C] mt-1.5 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                </AnimatedCard>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Faculty & Leave Section ──────────────────────────────────── */}
      <section id="faculty" className="py-24 px-6" data-testid="faculty-section">
        <div className="max-w-7xl mx-auto space-y-14">
          <SectionHeader
            badge="Faculty & Leave"
            badgeColor="bg-[#3B4E7A]/10 text-[#3B4E7A]"
            title="Faculty lifecycle — sessions, leave, conferences, and effectiveness"
            subtitle="Faculty aren't just attendance markers. Track their session delivery, leave history, conference participation, and teaching effectiveness — all inside the same platform."
          />

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: CalendarDays, title: 'Faculty Sessions', desc: 'Faculty log their own sessions and attendance delivery. Admins see coverage gaps in real time.', color: 'bg-[#3B4E7A]/10 text-[#3B4E7A]' },
              { icon: ClipboardCheck, title: 'Leave Management', desc: 'Apply for leave, attach documents, track approvals. Students and faculty share the same streamlined portal.', color: 'bg-[#6B3E26]/10 text-[#6B3E26]' },
              { icon: Award, title: 'Conferences & CPD', desc: 'Log conference attendance, paper presentations, and professional development — tied to individual faculty profiles.', color: 'bg-[#265C35]/10 text-[#265C35]' },
              { icon: Activity, title: 'Effectiveness Reports', desc: 'Aggregate session delivery, leave patterns, and feedback results into faculty effectiveness dashboards for admins.', color: 'bg-[#7A2D20]/10 text-[#7A2D20]' },
            ].map((f, i) => (
              <AnimatedCard key={f.title} delay={i * 0.08}>
                <div className="rounded-[1.5rem] border border-stone-200/80 bg-white p-5 flex flex-col gap-3 h-full hover:shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:border-stone-300 transition-all duration-300">
                  <div className={`w-10 h-10 rounded-xl ${f.color} flex items-center justify-center`}>
                    <f.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-[#1A1C1A]">{f.title}</h3>
                    <p className="text-xs text-[#5C605C] mt-1.5 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              </AnimatedCard>
            ))}
          </div>

          <div className="grid gap-5 lg:grid-cols-3">
            {[
              { icon: Bell, title: 'Smart Notifications', desc: 'Digest emails, low-attendance alerts, and session reminders delivered to the right role at the right time.', accent: 'border-l-4 border-l-amber-400' },
              { icon: AlertTriangle, title: 'Early Warning Engine', desc: 'Risk scoring across attendance, marks, and wellness flags students before they fall through the cracks.', accent: 'border-l-4 border-l-red-400' },
              { icon: Shield, title: 'Audit & Compliance', desc: 'Every attendance edit, eligibility snapshot, and student record change is logged for institutional compliance.', accent: 'border-l-4 border-l-[#1F3C2F]' },
            ].map((f, i) => (
              <AnimatedCard key={f.title} delay={0.3 + i * 0.08}>
                <div className={`rounded-[1.5rem] border border-stone-200/80 bg-[#FCFCFA] p-5 flex gap-4 ${f.accent} hover:shadow-[0_4px_20px_rgba(0,0,0,0.04)] transition-all duration-300`}>
                  <div className="w-10 h-10 rounded-xl bg-stone-100 text-stone-600 flex items-center justify-center shrink-0">
                    <f.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-[#1A1C1A]">{f.title}</h3>
                    <p className="text-xs text-[#5C605C] mt-1.5 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* ── Feedback & Survey Engine ──────────────────────────────────── */}
      <section id="feedback" className="py-24 px-6 bg-white" data-testid="feedback-section">
        <div className="max-w-7xl mx-auto space-y-14">
          <SectionHeader
            badge="Feedback & Survey Engine"
            badgeColor="bg-[#4A2D82]/10 text-[#4A2D82]"
            title="Anonymous course and faculty feedback — with threshold-protected results"
            subtitle="Build reusable survey templates, launch them to specific batches, and collect anonymous responses. Results are suppressed until the minimum threshold is met — protecting respondent identity before releasing to faculty."
          />

          <div className="grid gap-10 lg:grid-cols-2 items-start">
            <AnimatedCard whileHover={false}>
              <div className="rounded-[1.75rem] border border-stone-200/80 bg-white overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
                <div className="bg-gradient-to-r from-[#4A2D82] to-[#7B52C5] px-6 py-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.14em] text-white/70">Survey Results · Admin View</p>
                    <p className="text-white font-medium mt-0.5" style={{ fontFamily: 'Outfit' }}>General Medicine — Course Feedback</p>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-xs bg-white/20 text-white px-3 py-1 rounded-full backdrop-blur-sm">50 responses</span>
                    <span className="text-xs bg-green-400/30 text-white px-3 py-1 rounded-full backdrop-blur-sm">Released</span>
                  </div>
                </div>

                <div className="p-5 space-y-5">
                  {MOCK_FEEDBACK_RESULTS.map((q, i) => {
                    const max = Math.max(...q.dist);
                    return (
                      <div key={i} className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-[#1A1C1A]">{q.label}</span>
                          <span className="text-sm font-semibold text-[#4A2D82]">{q.mean} / 5</span>
                        </div>
                        <div className="flex gap-1.5 h-14 items-end">
                          {q.dist.map((count, j) => {
                            const pct = max > 0 ? (count / max) * 100 : 0;
                            const isHighest = count === max && count > 0;
                            return (
                              <div key={j} className="flex-1 flex flex-col items-center gap-0.5">
                                <span className="text-[9px] text-[#9CA3AF] leading-none">{count}</span>
                                <motion.div
                                  className={`w-full rounded-t-sm ${isHighest ? 'bg-[#4A2D82]/60' : 'bg-[#4A2D82]/20'}`}
                                  initial={{ height: 0 }}
                                  whileInView={{ height: `${pct}%`, minHeight: count > 0 ? '4px' : '0' }}
                                  viewport={{ once: true }}
                                  transition={{ duration: 0.8, delay: 0.3 + j * 0.05, ease: [0.21, 0.47, 0.32, 0.98] }}
                                />
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex gap-1.5">
                          {q.dist.map((_, j) => (
                            <div key={j} className="flex-1 text-center">
                              <span className="text-[9px] text-[#9CA3AF]">★{j + 1}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="px-5 py-3 bg-[#F5F0FF] border-t border-[#E8DEFF] flex items-center justify-between">
                  <span className="text-xs text-[#4A2D82]">Response rate: 83% · Threshold: 10 responses</span>
                  <button className="text-xs bg-[#4A2D82] text-white px-4 py-1.5 rounded-lg font-medium hover:bg-[#5A3A92] transition-colors">
                    Release to Faculty
                  </button>
                </div>
              </div>
            </AnimatedCard>

            <div className="space-y-4">
              {[
                { icon: ClipboardList, title: 'Reusable Survey Templates', desc: 'Build once — launch many times. Course and faculty templates with rating, MCQ, yes/no, and open-text questions.' },
                { icon: MessageSquare, title: 'Anonymous by Design', desc: 'No student IDs are stored with responses. SHA-256 submission tokens prevent duplicates without linking identity.' },
                { icon: Shield, title: 'Threshold Protection', desc: 'Results are hidden until a minimum response count is reached — protecting small group respondents from identification.' },
                { icon: TrendingUp, title: 'Admin Release Controls', desc: 'Admins see full aggregated results first. Faculty only see results after the admin explicitly releases them.' },
              ].map((f, i) => (
                <AnimatedCard key={f.title} delay={i * 0.08}>
                  <div className="rounded-[1.5rem] border border-stone-200/80 bg-[#FCFCFA] p-5 flex gap-4 hover:shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:border-stone-300 transition-all duration-300">
                    <div className="w-11 h-11 rounded-2xl bg-[#4A2D82]/10 text-[#4A2D82] flex items-center justify-center shrink-0">
                      <f.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-medium text-[#1A1C1A]" style={{ fontFamily: 'Outfit' }}>{f.title}</h3>
                      <p className="mt-1.5 text-sm text-[#5C605C] leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                </AnimatedCard>
              ))}

              <div className="grid grid-cols-3 gap-3 pt-1">
                {[
                  { val: 'Anonymous', label: 'Responses' },
                  { val: 'Threshold', label: 'Protection' },
                  { val: 'Controlled', label: 'Release' },
                ].map((stat, i) => (
                  <AnimatedCard key={stat.label} delay={0.4 + i * 0.08}>
                    <div className="rounded-2xl border border-[#E8DEFF] bg-[#F5F0FF] p-4 text-center">
                      <div className="text-sm font-semibold text-[#4A2D82]" style={{ fontFamily: 'Outfit' }}>{stat.val}</div>
                      <div className="text-xs text-[#7B52C5] mt-1">{stat.label}</div>
                    </div>
                  </AnimatedCard>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Platform / Why It Fits ───────────────────────────────────── */}
      <section id="platform" className="py-24 px-6" data-testid="platform-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <BlurFade delay={0.1} className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-[1.75rem] border border-stone-200/80 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
              <motion.div whileHover={{ scale: 1.03, transition: { duration: 0.5 } }}>
                <img src={CAMPUS_IMG} alt="Medical students" className="w-full h-[420px] object-cover" />
              </motion.div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1C1A]/55 via-transparent to-transparent" />
              <motion.div
                className="absolute left-5 right-5 bottom-5 rounded-2xl bg-white/92 backdrop-blur-md p-4 border border-white/20"
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3, ease: [0.21, 0.47, 0.32, 0.98] }}
              >
                <p className="text-xs uppercase tracking-[0.16em] text-[#7A7F7A]">Built for Scale</p>
                <p className="mt-2 text-lg font-medium text-[#1A1C1A]" style={{ fontFamily: 'Outfit' }}>
                  A shell that can grow with the institution, not just with one workflow.
                </p>
              </motion.div>
            </div>
          </BlurFade>

          <div className="lg:col-span-7 space-y-6">
            <BlurFade delay={0.1}>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-stone-400 mb-3">Why It Fits</div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1C1A]" style={{ fontFamily: 'Outfit' }}>
                The student becomes the center of the system
              </h2>
            </BlurFade>

            <div className="grid gap-4">
              {valuePillars.map((pillar, i) => (
                <AnimatedCard key={pillar.title} delay={0.2 + i * 0.08}>
                  <div className="rounded-[1.5rem] border border-stone-200/80 bg-[#FCFCFA] p-5 flex gap-4 hover:shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:border-stone-300 transition-all duration-300">
                    <div className="w-11 h-11 rounded-2xl bg-[#1F3C2F]/10 text-[#1F3C2F] flex items-center justify-center shrink-0">
                      <pillar.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-medium text-[#1A1C1A]" style={{ fontFamily: 'Outfit' }}>{pillar.title}</h3>
                      <p className="mt-1.5 text-sm text-[#5C605C] leading-relaxed">{pillar.desc}</p>
                    </div>
                  </div>
                </AnimatedCard>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-4 pt-2">
              {[
                { val: 360, label: 'Student View', suffix: '°' },
                { val: 12, label: 'Core Modules' },
                { val: 6, label: 'User Roles' },
              ].map((stat, i) => (
                <AnimatedCard key={stat.label} delay={0.5 + i * 0.08}>
                  <div className="rounded-2xl border border-stone-200/80 bg-white p-4 text-center">
                    <div className="text-2xl font-semibold text-[#1F3C2F]" style={{ fontFamily: 'Outfit' }}>
                      <NumberTicker value={stat.val} suffix={stat.suffix || ''} delay={0.3} />
                    </div>
                    <div className="text-xs text-[#5C605C] mt-1">{stat.label}</div>
                  </div>
                </AnimatedCard>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Workflow ──────────────────────────────────────────────────── */}
      <section id="workflow" className="py-24 px-6 bg-white" data-testid="workflow-section">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            badge="Workflow"
            badgeColor="bg-stone-100 text-stone-500"
            title="Start with attendance. Layer in IA, faculty, and feedback."
            className="mb-14"
          />

          <div className="grid gap-5 lg:grid-cols-2">
            {workflow.map((item, i) => (
              <AnimatedCard key={item.step} delay={i * 0.1}>
                <div className="rounded-[1.5rem] border border-stone-200/80 bg-[#FCFCFA] p-6 flex gap-5 h-full hover:shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:border-stone-300 transition-all duration-300">
                  <span className="text-4xl font-light text-[#6E8F7E] shrink-0 tabular-nums" style={{ fontFamily: 'Outfit' }}>{item.step}</span>
                  <div>
                    <h3 className="text-lg font-medium text-[#1A1C1A]" style={{ fontFamily: 'Outfit' }}>{item.title}</h3>
                    <p className="mt-2 text-sm text-[#5C605C] leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </AnimatedCard>
            ))}
          </div>

          <BlurFade delay={0.5}>
            <div className="mt-14 rounded-[2rem] border border-stone-200/80 bg-gradient-to-r from-[#F5F8F4] via-white to-[#F0EBFF] p-6 lg:p-8 grid gap-6 lg:grid-cols-[1.2fr_1fr] shadow-[0_4px_24px_rgba(0,0,0,0.03)]">
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-[#7A7F7A]">Platform Status</p>
                <h3 className="mt-2 text-2xl font-medium text-[#1A1C1A]" style={{ fontFamily: 'Outfit' }}>
                  Attendance, IA, faculty, CRMI, feedback, leave, early warning, and timetabling — all live.
                </h3>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  { icon: Stethoscope, label: 'Clinical operations aware' },
                  { icon: MessageSquare, label: 'Anonymous feedback engine' },
                  { icon: GraduationCap, label: 'IA marks and eligibility' },
                  { icon: Briefcase, label: 'Full faculty lifecycle' },
                  { icon: AlertTriangle, label: 'Early warning system' },
                  { icon: Users, label: 'Role-based access for 6 roles' },
                ].map((item, i) => (
                  <AnimatedCard key={item.label} delay={0.6 + i * 0.05}>
                    <div className="rounded-2xl border border-stone-200/80 bg-white/80 backdrop-blur-sm p-3 flex items-start gap-3 hover:bg-white transition-colors duration-300">
                      <item.icon className="w-4 h-4 text-[#1F3C2F] mt-0.5 shrink-0" />
                      <span className="text-sm text-[#1A1C1A]">{item.label}</span>
                    </div>
                  </AnimatedCard>
                ))}
              </div>
            </div>
          </BlurFade>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────── */}
      <section className="py-24 px-6 relative overflow-hidden" data-testid="cta-section">
        {/* Subtle radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(31,60,47,0.06),_transparent_60%)]" />

        <div className="relative max-w-4xl mx-auto text-center space-y-6">
          <BlurFade delay={0.1}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1F3C2F]/8 border border-[#1F3C2F]/10 text-[#1F3C2F] text-xs tracking-[0.15em] uppercase font-bold">
              <Sparkles className="w-3 h-3" />
              Ready for Cliniq 360
            </div>
          </BlurFade>
          <BlurFade delay={0.2}>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1C1A]" style={{ fontFamily: 'Outfit' }}>
              Launch the platform that connects attendance, IA, faculty, feedback, and the wider student journey.
            </h2>
          </BlurFade>
          <BlurFade delay={0.3}>
            <p className="text-base text-[#5C605C] max-w-2xl mx-auto leading-relaxed">
              Move from a single attendance workflow to an institution-wide operating system across 12 modules — academics, assessments, faculty, research, postgraduate training, feedback, early warning, and governance.
            </p>
          </BlurFade>
          <BlurFade delay={0.4}>
            <Magnetic strength={0.15}>
              <Button
                data-testid="cta-get-started-btn"
                className="bg-[#1F3C2F] hover:bg-[#325744] text-white h-12 px-8 text-base shadow-[0_1px_3px_rgba(31,60,47,0.3),0_8px_24px_rgba(31,60,47,0.15),inset_0_1px_0_rgba(255,255,255,0.1)] hover:shadow-[0_1px_3px_rgba(31,60,47,0.3),0_12px_32px_rgba(31,60,47,0.2),inset_0_1px_0_rgba(255,255,255,0.1)] transition-shadow"
                onClick={() => navigate('/login')}
              >
                Launch Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Magnetic>
          </BlurFade>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <footer className="py-8 px-6 border-t border-stone-200/80 bg-white">
        <div className="max-w-7xl mx-auto flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#1F3C2F]" />
            <span className="text-sm font-medium text-[#1A1C1A]" style={{ fontFamily: 'Outfit' }}>CliniQ 360</span>
          </div>
          <p className="text-xs text-[#5C605C]">Operations Platform for medical colleges — 12 modules, one coherent system</p>
        </div>
      </footer>
    </div>
  );
}
